import {
  DT_STEP_MS,
  LOG_VERSION,
  OP_KINDS,
  PAUSE_GRANULARITY_S,
  type KeystrokeLog,
  type LogOp,
} from './types'
import { ByteReader, ByteWriter } from './varint'

const MAGIC = new Uint8Array([0x43, 0x52, 0x4c, 0x31]) // "CRL1"
const FLAG_ASSISTED = 1

function writeOp(w: ByteWriter, op: LogOp): void {
  if (op.dt % DT_STEP_MS !== 0)
    throw new RangeError(`dt ${op.dt} is not a multiple of ${DT_STEP_MS}`)
  if (op.to < op.from) throw new RangeError(`op range ${op.from}..${op.to} is inverted`)
  const kind = OP_KINDS.indexOf(op.kind)
  if (kind < 0) throw new RangeError(`unknown op kind ${op.kind}`)
  const pause = op.pause === undefined ? 0 : op.pause / PAUSE_GRANULARITY_S
  w.uint(op.dt / DT_STEP_MS)
    .uint(op.from)
    .uint(op.to - op.from)
    .uint(kind)
    .uint(pause)
    .string(op.text)
}

function readOp(r: ByteReader): LogOp {
  const dt = r.uint() * DT_STEP_MS
  const from = r.uint()
  const to = from + r.uint()
  const kind = OP_KINDS[r.uint()]
  if (kind === undefined) throw new RangeError('unknown op kind in log')
  const pause = r.uint() * PAUSE_GRANULARITY_S
  const text = r.string()
  return pause > 0 ? { dt, from, to, text, kind, pause } : { dt, from, to, text, kind }
}

export function encodeRaw(log: KeystrokeLog): Uint8Array {
  const w = new ByteWriter()
    .raw(MAGIC)
    .uint(log.createdAt)
    .uint(log.assisted ? FLAG_ASSISTED : 0)
    .uint(log.ops.length)
  for (const op of log.ops) writeOp(w, op)
  return w.bytes()
}

export function decodeRaw(bytes: Uint8Array): KeystrokeLog {
  const r = new ByteReader(bytes)
  const magic = r.raw(MAGIC.length)
  if (!MAGIC.every((b, i) => magic[i] === b)) throw new RangeError('bad magic: not a CRL1 log')
  const createdAt = r.uint()
  const assisted = (r.uint() & FLAG_ASSISTED) !== 0
  const count = r.uint()
  if (count > MAX_OPS) throw new RangeError(`log declares ${count} ops, over the ${MAX_OPS} cap`)
  const ops = Array.from({ length: count }, () => readOp(r))
  return { version: LOG_VERSION, createdAt, assisted, ops }
}

type ByteStream = ReadableWritablePair<Uint8Array, Uint8Array>

function byteStream(bytes: Uint8Array): ReadableStream<Uint8Array> {
  return new ReadableStream({
    start(controller) {
      controller.enqueue(bytes)
      controller.close()
    },
  })
}

/** Upper bound on an inflated log: a full plot with heavy editing is well under 1 MB. */
export const MAX_DECODED_BYTES = 4 * 1024 * 1024
/** Upper bound on ops per log, so a hostile header cannot force a giant allocation. */
export const MAX_OPS = 200_000

async function pipeThrough(
  bytes: Uint8Array,
  stream: CompressionStream | DecompressionStream,
  maxBytes: number,
): Promise<Uint8Array> {
  // The platform typings declare the writable side as BufferSource; we only ever feed it bytes.
  const reader = byteStream(bytes)
    .pipeThrough(stream as unknown as ByteStream)
    .getReader()
  const chunks: Uint8Array[] = []
  let total = 0
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    total += value.length
    if (total > maxBytes) {
      await reader.cancel()
      throw new RangeError(`decoded log exceeds ${maxBytes} bytes`)
    }
    chunks.push(value)
  }
  return concat(chunks, total)
}

function concat(chunks: readonly Uint8Array[], total: number): Uint8Array {
  const out = new Uint8Array(total)
  let offset = 0
  for (const chunk of chunks) {
    out.set(chunk, offset)
    offset += chunk.length
  }
  return out
}

export function encodeLog(log: KeystrokeLog): Promise<Uint8Array> {
  return pipeThrough(encodeRaw(log), new CompressionStream('gzip'), Number.POSITIVE_INFINITY)
}

export async function decodeLog(bytes: Uint8Array): Promise<KeystrokeLog> {
  return decodeRaw(await pipeThrough(bytes, new DecompressionStream('gzip'), MAX_DECODED_BYTES))
}
